// utils/timestamp.ts

/**
 * Converts a Unix timestamp (seconds since epoch) to a formatted object
 * @param timestamp - Unix timestamp in seconds (or milliseconds if isMilliseconds is true)
 * @param isMilliseconds - Set to true if timestamp is in milliseconds (default: false)
 * @returns Object with formatted date and clock strings
 */
export function formatTimestamp(
  timestamp: number,
  isMilliseconds: boolean = false,
): { date: string; clock: string } {
  if (!timestamp && timestamp !== 0) {
    return { date: "N/A", clock: "N/A" };
  }

  const date = new Date(isMilliseconds ? timestamp : timestamp * 1000);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return { date: "Invalid Date", clock: "Invalid Time" };
  }

  // Format date: e.g., "Jan 15, 2024"
  const dateString = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  // Format clock: e.g., "14:30" (24-hour format)
  const clockString = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return { date: dateString, clock: clockString };
}

export const formatDate = (dateString: string | number) => {
  const date =
    typeof dateString === "number"
      ? new Date(dateString * 1000) // Convert seconds to milliseconds
      : new Date(dateString);

  return {
    date: date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
    time: date.toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false, // Use 24-hour format (hh:mm)
    }),
  };
};
/**
 * Returns relative time (e.g., "2 hours ago", "3 days ago")
 * @param timestamp - Unix timestamp in seconds
 * @returns Human-readable relative time string
 */
export function formatRelativeTime(timestamp: number): string {
  if (!timestamp) return "N/A";

  const now = Date.now();
  const diffSeconds = Math.floor((now - timestamp * 1000) / 1000);

  if (diffSeconds < 60) {
    return `${diffSeconds} seconds ago`;
  }

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) {
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  }

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths > 1 ? "s" : ""} ago`;
  }

  const diffYears = Math.floor(diffMonths / 12);
  return `${diffYears} year${diffYears > 1 ? "s" : ""} ago`;
}

export function getInitials(str?: string) {
  if (!str) return "";
  return str.slice(0, 2).toUpperCase();
}
